import { BranchCode, StepIdentifier } from "api/risk";
import { BorrowerStepIdentifier, LegalProtectionStepIdentifier, MriStepIdentifier, RGIStepIdentifier, StepIdentifier as LocalStepIdentifier, UnemploymentStepIdentifier, UPIStepIdentifier } from "../types/enums";
import { LEGAL_PROTECTION_ROUTE_LABELS, LEGAL_PROTECTION_ROUTE_TEMPLATES } from "./legalProtectionRoutes";
import { MRI_ROUTE_LABELS, MRI_ROUTE_TEMPLATES } from "./mriRoutes";

export type RouteConfigItem = {
  route: string;
  label: string;
};

export enum ExclusionKey {
  Person = 'person',
  RealEstate = 'realEstate',
  Tenant = 'tenant',
  Pricing = 'pricing',
  IsEligible = 'isEligible',
  IsPricingCompleted = 'isPricingCompleted',
  IsContractualizationCompleted = 'isContractualizationCompleted',
}

type RouteConfig = Record<BranchCode, Partial<Record<number, RouteConfigItem>>>;
type RouteExclusionConfig = Record<BranchCode, Partial<Record<number, ExclusionKey>>>;

const ROUTE_MAPPING: RouteConfig = {
  [BranchCode.UPI]: {
    [UPIStepIdentifier.Registration]: {
      route: "/simulation/inscription/cbno/:riskId",
      label: "Gérer l'assuré"
    },
    [UPIStepIdentifier.RealEstateRegistration]: {
      route: "/simulation/inscription/cbno/biens/:riskId",
      label: "Gérer le bien immobilier"
    },
    [UPIStepIdentifier.Pricing]: {
      route: "/simulation/tarification/:riskId",
      label: "Tarification"
    },
    [UPIStepIdentifier.Contractualization]: {
      route: "/simulation/contractualisation/:quoteId",
      label: "Contractualisation"
    },
  },
  [BranchCode.Unemployment]: {
    [UnemploymentStepIdentifier.Registration]: {
      route: "/simulation/inscription/chomage/:riskId",
      label: "Gérer l'assuré"
    },
    [UnemploymentStepIdentifier.Profiling]: {
      route: "/simulation/profilage/chomage/:riskId",
      label: "Profilage"
    },
    [UnemploymentStepIdentifier.Pricing]: {
      route: "/simulation/tarification/:riskId",
      label: "Tarification"
    },
    [UnemploymentStepIdentifier.Contractualization]: {
      route: "/simulation/contractualisation/:quoteId",
      label: "Contractualisation"
    },
  },
  [BranchCode.Borrower]: {
    [BorrowerStepIdentifier.Project]: {
      route: "/simulation/profilage/emprunteur/projet/:borrowerProjectId",
      label: "Gérer le projet"
    },
    [BorrowerStepIdentifier.Pricing]: {
      route: "/simulation/tarification/emprunteur/:borrowerProjectId/:borrowerId?",
      label: "Tarification"
    },
    [BorrowerStepIdentifier.Contractualization]: {
      route: "/simulation/contractualisation/borrower/:borrowerProjectId/:borrowerId?/:quoteId",
      label: "Contractualisation"
    },
  },
  [BranchCode.RGI]: {
    [RGIStepIdentifier.Registration]: {
      route: "/simulation/inscription/gli/:riskId",
      label: "Gérer l'assuré"
    },
    [RGIStepIdentifier.RealEstateRegistration]: {
      route: "/simulation/inscription/gli/biens/:riskId",
      label: "Gérer le bien immobilier"
    },
    [RGIStepIdentifier.TenantRegistration]: {
      route: "/simulation/inscription/tenants/:riskId",
      label: "Gérer les locataires"
    },
    [RGIStepIdentifier.Pricing]: {
      route: "/simulation/tarification/:riskId",
      label: "Tarification"
    },
    [RGIStepIdentifier.Contractualization]: {
      route: "/simulation/contractualisation/:quoteId",
      label: "Contractualisation"
    },
  },
  [BranchCode.Mri]: {
    [MriStepIdentifier.Registration]: {
      route: MRI_ROUTE_TEMPLATES.registration,
      label: MRI_ROUTE_LABELS.registration
    },
    [MriStepIdentifier.CopropertyRegistration]: {
      route: MRI_ROUTE_TEMPLATES.copropertyRegistration,
      label: MRI_ROUTE_LABELS.copropertyRegistration
    },
    [MriStepIdentifier.CopropertySurvey]: {
      route: MRI_ROUTE_TEMPLATES.copropertySurvey,
      label: MRI_ROUTE_LABELS.copropertySurvey
    },
    [MriStepIdentifier.CopropertyBuildings]: {
      route: MRI_ROUTE_TEMPLATES.copropertyBuildings,
      label: MRI_ROUTE_LABELS.copropertyBuildings
    },
    [MriStepIdentifier.CopropertyComplements]: {
      route: MRI_ROUTE_TEMPLATES.copropertyComplements,
      label: MRI_ROUTE_LABELS.copropertyComplements
    },
    [MriStepIdentifier.CopropertyClaims]: {
      route: MRI_ROUTE_TEMPLATES.copropertyClaims,
      label: MRI_ROUTE_LABELS.copropertyClaims
    },
    [MriStepIdentifier.Pricing]: {
      route: MRI_ROUTE_TEMPLATES.pricing,
      label: MRI_ROUTE_LABELS.pricing
    },
    [MriStepIdentifier.Contractualization]: {
      route: "/simulation/contractualisation/:quoteId",
      label: "Contractualisation"
    },
  },
  [BranchCode.Pet]: {}, // Souscription portée par l'espace-membre, pas le courtier
  [BranchCode.LegalProtection]: {
    [LegalProtectionStepIdentifier.Registration]: {
      route: LEGAL_PROTECTION_ROUTE_TEMPLATES.registration,
      label: LEGAL_PROTECTION_ROUTE_LABELS.registration
    },
    // The eligibility is part of the registration page: same route as the registration.
    [LegalProtectionStepIdentifier.Eligibility]: {
      route: LEGAL_PROTECTION_ROUTE_TEMPLATES.registration,
      label: LEGAL_PROTECTION_ROUTE_LABELS.registration
    },
    [LegalProtectionStepIdentifier.Pricing]: {
      route: LEGAL_PROTECTION_ROUTE_TEMPLATES.pricing,
      label: LEGAL_PROTECTION_ROUTE_LABELS.pricing
    },
    [LegalProtectionStepIdentifier.Contractualization]: {
      route: "/simulation/contractualisation/:riskId/:quoteId",
      label: "Contractualisation"
    },
  },
};

const ROUTE_EXCLUSION: RouteExclusionConfig = {
  [BranchCode.UPI]: {
    [UPIStepIdentifier.Registration]: ExclusionKey.Person,
    [UPIStepIdentifier.RealEstateRegistration]: ExclusionKey.RealEstate,
    [UPIStepIdentifier.Pricing]: ExclusionKey.IsPricingCompleted,
    [UPIStepIdentifier.Contractualization]: ExclusionKey.IsContractualizationCompleted,
  },
  [BranchCode.Unemployment]: {
    [UnemploymentStepIdentifier.Registration]: ExclusionKey.Person,
    [UnemploymentStepIdentifier.Profiling]: ExclusionKey.IsEligible,
    [UnemploymentStepIdentifier.Pricing]: ExclusionKey.Pricing,
    [UnemploymentStepIdentifier.Contractualization]: ExclusionKey.IsContractualizationCompleted,
  },
  [BranchCode.Borrower]: {
  },
  [BranchCode.RGI]: {
    [RGIStepIdentifier.Registration]: ExclusionKey.Person,
    [RGIStepIdentifier.RealEstateRegistration]: ExclusionKey.RealEstate,
    [RGIStepIdentifier.TenantRegistration]: ExclusionKey.Tenant,
    [RGIStepIdentifier.Pricing]: ExclusionKey.IsPricingCompleted,
    [RGIStepIdentifier.Contractualization]: ExclusionKey.IsContractualizationCompleted,
  },
  [BranchCode.Mri]: {}, // À compléter si nécessaire
  [BranchCode.Pet]: {}, // Souscription portée par l'espace-membre, pas le courtier
  [BranchCode.LegalProtection]: {},
};

/**
 * Retourne le template de route correspondant au branchCode et stepIdentifier donnés
 * Si la step a une clé d'exclusion présente dans le tableau excludedKeys, retourne null
 * @param branchCode - Le code de branche (UPI, Unemployment, Borrower, RGI, Mri)
 * @param stepIdentifier - L'identifiant de l'étape (enum local frontend)
 * @param excludedKeys - Tableau des clés d'exclusion (par défaut vide)
 * @returns Le template de route avec les paramètres ou null si exclu/non trouvé
 */
export const getStepRouteByStepIdentifier = (
  branchCode: BranchCode,
  stepIdentifier: LocalStepIdentifier,
  excludedKeys: ExclusionKey[] = []
): RouteConfigItem | null => {
  const exclusionKey = ROUTE_EXCLUSION[branchCode]?.[stepIdentifier];

  // Si la clé d'exclusion est dans les exclusions, retourner null
  if (exclusionKey && excludedKeys.includes(exclusionKey)) {
    return null;
  }

  const routeConfig = ROUTE_MAPPING[branchCode]?.[stepIdentifier];

  if (!routeConfig) {
    return null;
  }

  return routeConfig;
};

/**
 * Construit une route en remplaçant les paramètres du template par les valeurs fournies
 * @param template - Le template de route (ex: "/simulation/inscription/cbno/:riskId")
 * @param params - Un objet contenant les valeurs des paramètres (ex: { riskId: "123" })
 * @returns La route complète avec les paramètres remplacés
 * @throws Error si un paramètre requis n'est pas fourni
 */
export const buildRoute = (template: string, params: Record<string, string>): string => {
  let route = template;

  // Extraire tous les paramètres du template (ex: :riskId, :borrowerId?, etc.)
  const paramMatches = template.match(/:[a-zA-Z]+\??/g) || [];

  for (const paramMatch of paramMatches) {
    const isOptional = paramMatch.endsWith('?');
    const paramName = paramMatch.replace(/^:|(\?$)/g, '');

    if (paramName in params && params[paramName]) {
      route = route.replace(paramMatch, params[paramName]);
    } else if (isOptional) {
      // Supprimer le segment optionnel (/:param?) de la route
      route = route.replace(`/${paramMatch}`, '');
    } else {
      throw new Error(
        `Paramètre manquant: ${paramName} requis pour la route ${template}`
      );
    }
  }

  return route;
};

// Mapping from StepIdentifier (backend enum) to route configs
const STEP_IDENTIFIER_ROUTE_MAPPING: Record<BranchCode, Partial<Record<StepIdentifier, RouteConfigItem>>> = {
  [BranchCode.UPI]: {
    [StepIdentifier.FolderCreation]: { route: "/simulation/inscription/cbno/:riskId", label: "Gérer l'assuré" },
    [StepIdentifier.RealEstateRegistration]: { route: "/simulation/inscription/cbno/biens/:riskId", label: "Gérer le bien immobilier" },
    [StepIdentifier.Pricing]: { route: "/simulation/tarification/:riskId", label: "Tarification" },
    [StepIdentifier.Contractualization]: { route: "/simulation/contractualisation/:riskId/:quoteId", label: "Contractualisation" },
    [StepIdentifier.SignedContract]: { route: "/simulation/contractualisation/:riskId/:quoteId", label: "Contractualisation" },
  },
  [BranchCode.Unemployment]: {
    [StepIdentifier.FolderCreation]: { route: "/simulation/inscription/chomage/:riskId", label: "Gérer l'assuré" },
    [StepIdentifier.Profiling]: { route: "/simulation/profilage/chomage/:riskId", label: "Profilage" },
    [StepIdentifier.Pricing]: { route: "/simulation/tarification/:riskId", label: "Tarification" },
    [StepIdentifier.Contractualization]: { route: "/simulation/contractualisation/:riskId/:quoteId", label: "Contractualisation" },
    [StepIdentifier.SignedContract]: { route: "/simulation/contractualisation/:riskId/:quoteId", label: "Contractualisation" },
  },
  [BranchCode.Borrower]: {
    // Étapes back emprunteur : PROJECT -> PRICING -> CONTRACTUALIZATION -> SIGNED_CONTRACT
    [StepIdentifier.Project]: { route: "/simulation/profilage/emprunteur/projet/:borrowerProjectId", label: "Gérer le projet" },
    [StepIdentifier.FolderCreation]: { route: "/simulation/profilage/emprunteur/projet/:borrowerProjectId", label: "Gérer le projet" },
    [StepIdentifier.Pricing]: { route: "/simulation/tarification/emprunteur/:borrowerProjectId/:borrowerId?", label: "Tarification" },
    [StepIdentifier.Contractualization]: { route: "/simulation/contractualisation/borrower/:borrowerProjectId/:borrowerId?/:quoteId", label: "Contractualisation" },
    [StepIdentifier.SignedContract]: { route: "/simulation/contractualisation/borrower/:borrowerProjectId/:borrowerId?/:quoteId", label: "Contractualisation" },
  },
  [BranchCode.RGI]: {
    [StepIdentifier.FolderCreation]: { route: "/simulation/inscription/gli/:riskId", label: "Gérer l'assuré" },
    [StepIdentifier.RealEstateRegistration]: { route: "/simulation/inscription/gli/biens/:riskId", label: "Gérer le bien immobilier" },
    [StepIdentifier.TenantRegistration]: { route: "/simulation/inscription/tenants/:riskId", label: "Gérer les locataires" },
    [StepIdentifier.Pricing]: { route: "/simulation/tarification/:riskId", label: "Tarification" },
    [StepIdentifier.Contractualization]: { route: "/simulation/contractualisation/:riskId/:quoteId", label: "Contractualisation" },
    [StepIdentifier.SignedContract]: { route: "/simulation/contractualisation/:riskId/:quoteId", label: "Contractualisation" },
  },
  [BranchCode.Mri]: {
    [StepIdentifier.FolderCreation]: { route: MRI_ROUTE_TEMPLATES.registration, label: MRI_ROUTE_LABELS.registration },
    [StepIdentifier.CopropertyRegistration]: { route: MRI_ROUTE_TEMPLATES.copropertyRegistration, label: MRI_ROUTE_LABELS.copropertyRegistration },
    [StepIdentifier.CopropertySurvey]: { route: MRI_ROUTE_TEMPLATES.copropertySurvey, label: MRI_ROUTE_LABELS.copropertySurvey },
    [StepIdentifier.CopropertyBuildings]: { route: MRI_ROUTE_TEMPLATES.copropertyBuildings, label: MRI_ROUTE_LABELS.copropertyBuildings },
    [StepIdentifier.CopropertyComplements]: { route: MRI_ROUTE_TEMPLATES.copropertyComplements, label: MRI_ROUTE_LABELS.copropertyComplements },
    [StepIdentifier.Pricing]: { route: MRI_ROUTE_TEMPLATES.pricing, label: MRI_ROUTE_LABELS.pricing },
    [StepIdentifier.Contractualization]: { route: "/simulation/contractualisation/:riskId/:quoteId", label: "Contractualisation" },
    [StepIdentifier.SignedContract]: { route: "/simulation/contractualisation/:riskId/:quoteId", label: "Contractualisation" },
  },
  [BranchCode.Pet]: {}, // Souscription portée par l'espace-membre, pas le courtier
  [BranchCode.LegalProtection]: {
    [StepIdentifier.FolderCreation]: { route: LEGAL_PROTECTION_ROUTE_TEMPLATES.registration, label: LEGAL_PROTECTION_ROUTE_LABELS.registration },
    [StepIdentifier.Eligibility]: { route: LEGAL_PROTECTION_ROUTE_TEMPLATES.registration, label: LEGAL_PROTECTION_ROUTE_LABELS.registration },
    [StepIdentifier.Pricing]: { route: LEGAL_PROTECTION_ROUTE_TEMPLATES.pricing, label: LEGAL_PROTECTION_ROUTE_LABELS.pricing },
    [StepIdentifier.Contractualization]: { route: "/simulation/contractualisation/:riskId/:quoteId", label: "Contractualisation" },
    [StepIdentifier.SignedContract]: { route: "/simulation/contractualisation/:riskId/:quoteId", label: "Contractualisation" },
  },
};

/**
 * Retourne le template de route correspondant au branchCode et stepIdentifier (enum du backend)
 * @param branchCode - Le code de branche (UPI, Unemployment, Borrower, RGI, Mri)
 * @param stepIdentifier - L'identifiant de l'étape (StepIdentifier enum)
 * @returns Le template de route avec les paramètres ou null si non trouvé
 */
export const getStepRouteByStepIdentifierString = (
  branchCode: BranchCode,
  stepIdentifier: StepIdentifier
): RouteConfigItem | null => {
  return STEP_IDENTIFIER_ROUTE_MAPPING[branchCode]?.[stepIdentifier] ?? null;
};
